import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Card, InputGroup, FormControl, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faMapMarkedAlt,
  faUserPlus,
  faSignInAlt,
  faMapMarkerAlt,
  faUserShield,
  faHandshake,
  faSearch,
  faInfoCircle,
  faCheckCircle,
  faShieldAlt,
  faBriefcase,
  faDollarSign,
  faUserGraduate,
  faCogs,
  faChartLine,
  faHeartbeat,
  faUsers,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import './HomePage.css';
import ReactDOMServer from 'react-dom/server';

// Import Leaflet for map preview
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Import leaflet.heat
import 'leaflet.heat';

// Import the API service
import api from '../services/api';

// Hero section gradient style (similar to Flask)
const heroGradientStyle = {
  background: 'linear-gradient(135deg, #3498db 0%, #2ecc71 100%)',
};

// Define the type for the map data we expect from the API
interface MapData {
  center: { latitude: number; longitude: number };
  users: Array<{ id: number; latitude: number; longitude: number; name: string; profession?: string; distance_meters?: number }>;
  stats: {
    totalCount: number;
    professionCounts: Record<string, number>;
  };
}

// Helper component to handle map actions like flyTo, heatmap updates (NO MARKERS)
const MapController: React.FC<{ 
    center: L.LatLngTuple; 
    zoom: number; 
    mapData: MapData | null; 
    heatmapLayerRef: React.MutableRefObject<L.HeatLayer | null>;
}> = ({ center, zoom, mapData, heatmapLayerRef }) => {
    const map = useMap();

    // Effect for flying to new location
    useEffect(() => {
        if (map) {
             map.flyTo(center, zoom, {
                 animate: true,
                 duration: 1.5 // Adjust duration as needed
             });
        } 
    }, [center, zoom, map]);

    // Effect for managing heatmap layer (KEEP, but remove marker logic)
    useEffect(() => {
        if (!map) {
            return;
        }

        // --- Manage Heatmap Layer ---
        const currentHeatmapLayer = heatmapLayerRef.current;
        if (mapData?.users && mapData.users.length > 0) {
            // Explicitly cast the points array to the type expected by heatLayer
            const points = mapData.users.map(
                user => [user.latitude, user.longitude, 1] // lat, lng, intensity
            ) as L.HeatLatLngTuple[]; // Cast to HeatLatLngTuple array
            
            if (!currentHeatmapLayer) {
                heatmapLayerRef.current = L.heatLayer(points, { radius: 25 }).addTo(map);
            } else {
                currentHeatmapLayer.setLatLngs(points);
                if (!map.hasLayer(currentHeatmapLayer)) {
                     map.addLayer(currentHeatmapLayer);
                }
            }
        } else {
            if (currentHeatmapLayer && map.hasLayer(currentHeatmapLayer)) {
                map.removeLayer(currentHeatmapLayer);
                heatmapLayerRef.current = null; 
            }
        }

    }, [map, mapData, heatmapLayerRef]); 

    return null; // This component does not render anything itself
};

// --- Custom Icon Logic ---

// 1. Define Profession to Icon mapping
interface ProfessionIconInfo {
  icon: IconDefinition;
  color: string;
}

const getProfessionIcon = (profession?: string): ProfessionIconInfo => {
  const normalizedProfession = profession?.toLowerCase() || 'unknown';
  switch (normalizedProfession) {
    case 'engineering':
      return { icon: faCogs, color: '#6c757d' }; // Grey
    case 'data scientist':
    case 'analyst':
    case 'business':
      return { icon: faBriefcase, color: '#0d6efd' }; // Blue
    case 'finance':
      return { icon: faDollarSign, color: '#198754' }; // Green
    case 'education':
      return { icon: faUserGraduate, color: '#ffc107' }; // Yellow
    case 'sales':
      return { icon: faChartLine, color: '#dc3545' }; // Red
    case 'healthcare':
      return { icon: faHeartbeat, color: '#fd7e14' }; // Orange
    case 'human_resources':
      return { icon: faUsers, color: '#6f42c1' }; // Purple
    default:
      return { icon: faQuestionCircle, color: '#adb5bd' }; // Light Grey
  }
};

// 2. Function to create L.DivIcon
const createDivIcon = (iconInfo: ProfessionIconInfo): L.DivIcon => {
  const iconHtml = ReactDOMServer.renderToString(
    <FontAwesomeIcon icon={iconInfo.icon} />
  );

  return L.divIcon({
    html: `<span class="map-marker-icon" style="background-color: ${iconInfo.color};">${iconHtml}</span>`,
    className: '', // Important to clear default styles
    iconSize: [30, 30], // Size of the icon
    iconAnchor: [15, 30], // Point of the icon which will correspond to marker's location (bottom center)
    popupAnchor: [0, -30] // Point from which the popup should open relative to the iconAnchor (top center)
  });
};

// --- End Custom Icon Logic ---

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // --- State Variables --- 
  const [zipCode, setZipCode] = useState<string>('');
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const heatmapLayerRef = useRef<L.HeatLayer | null>(null); // Ref to hold the heatmap layer instance
  // ---------------------

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Default center for map preview
  const defaultMapCenter: L.LatLngTuple = [39.8283, -98.5795]; 
  // Dynamic center based on search results
  const currentMapCenter: L.LatLngTuple = mapData 
    ? [mapData.center.latitude, mapData.center.longitude] 
    : defaultMapCenter;
  // Zoom level - Zoom in further when results are shown
  const currentMapZoom = mapData ? 12 : 3; // INCREASED zoom from 11 to 13

  // --- API Call Function --- 
  const handleZipSearch = async () => {
    // Validate zip code (5 digits)
    if (!/^\d{5}$/.test(zipCode)) {
      setSearchError('Please enter a valid 5-digit US zip code.');
      setMapData(null); // Clear previous results on new invalid search
      return;
    }

    setSearchError(null);
    setIsLoading(true);
    setMapData(null); // Clear previous results before new search

    try {
      // Use the configured Axios instance (api)
      // The base URL (http://localhost:3001/api) is already set in api.ts
      const endpoint = `/users/search/zipcode/${zipCode}`;
      console.log(`[HomePage] Fetching via Axios: ${endpoint}`);
      // Use the api service here
      const response = await api.get(endpoint); 
      const data = response.data; // Axios automatically parses JSON and puts it in response.data

      // Axios throws an error for non-2xx responses, 
      // so we primarily check the success flag from our backend response structure.
      if (!data.success) {
        throw new Error(data.message || 'Backend indicated failure but provided no message.');
      }
      
      console.log("[HomePage] Received map data:", data.data);
      setMapData(data.data as MapData); // Set the received data

    } catch (error: any) {
      console.error("[HomePage] Zip search error:", error);
      // Handle Axios errors (error.response.data might contain backend message)
      // Handle non-Axios errors (e.g., network error before request was sent)
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred during the search.';
      setSearchError(errorMessage);
      setMapData(null); // Clear map data on error
    } finally {
      setIsLoading(false);
    }
  };
  // ----------------------

  return (
    <MainLayout>
      {/* Hero Section - Adapted from Flask */} 
      <section className="py-5 position-relative hero-section-flask" style={heroGradientStyle}>
          <Container className="py-lg-5"> {/* Added more padding on large screens */} 
              <Row className="align-items-center">
                  {/* Left Column: Text & Buttons */}
                  <Col lg={6} className="text-white mb-5 mb-lg-0">
                      <h1 className="display-4 fw-bold mb-4 slide-up">Connect with Data Professionals in Your Area</h1>
                      <p className="lead mb-4 slide-up">
                         Discover a privacy-first network that helps you find and connect with nearby data scientists, analysts, and engineers for collaboration, mentorship, or simply a coffee chat.
                      </p>
                      <div className="d-flex gap-3 slide-up">
                          <Button 
                              onClick={() => handleNavigation('/register')}
                              variant="light" 
                              size="lg" 
                              className="rounded-pill px-4 shadow-sm"
                          >
                              <FontAwesomeIcon icon={faUserPlus} className="me-2" /> Join the Network
                          </Button>
                          {/* Simple anchor link for smooth scrolling to the section */}
                          <a href="#how-it-works" className="btn btn-outline-light btn-lg rounded-pill px-4">
                              Learn More
                          </a>
                      </div>
                  </Col>
                  
                  {/* Right Column: Card with Search & Map Preview */} 
                  <Col lg={6}>
                      <Card className="border-0 shadow-lg rounded-4 overflow-hidden slide-up">
                          <Card.Body className="p-0">
                              {/* Zip Code Search - Now interactive */}
                              <div className="p-3 bg-light border-bottom">
                                  <InputGroup>
                                      <FormControl 
                                          type="text" 
                                          placeholder="Enter 5-digit US Zip Code" 
                                          aria-label="Enter Zip Code" 
                                          value={zipCode}
                                          onChange={(e) => setZipCode(e.target.value)}
                                          onKeyDown={(e) => e.key === 'Enter' && handleZipSearch()} // Allow Enter key submission
                                          disabled={isLoading} // Disable during loading
                                          maxLength={5} // Enforce max length
                                      />
                                      <Button 
                                        variant="primary" 
                                        type="button" 
                                        onClick={handleZipSearch}
                                        disabled={isLoading || zipCode.length !== 5} // Disable during loading or if zip is not 5 digits
                                      >
                                        {isLoading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faSearch} />}
                                        {isLoading ? ' Loading...' : ' Search'} 
                                      </Button>
                                  </InputGroup>
                                  {/* Display Search Error */} 
                                  {searchError && (
                                      <Alert variant="danger" className="mt-2 mb-0 py-1 px-2 small">
                                         {searchError}
                                      </Alert>
                                  )}
                              </div>
                              {/* Map Preview - Updated props and includes MapController */}
                              <div id="map-preview" className="map-preview" style={{ height: '300px', width: '100%' }}>
                                 <MapContainer 
                                      center={currentMapCenter} 
                                      zoom={currentMapZoom} 
                                      scrollWheelZoom={false} // Keep scroll wheel zoom disabled for preview
                                      dragging={true} // << ENABLE dragging
                                      zoomControl={false} // << DISABLE zoom control
                                      doubleClickZoom={true} // Allow double click zoom
                                      touchZoom={true} // Allow touch zoom on mobile
                                      style={{ height: '100%', width: '100%' }}
                                  >
                                      <TileLayer
                                         url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                                         attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                                      />
                                      
                                      {/* MapController handles flyTo and heatmap */}
                                      <MapController 
                                        center={currentMapCenter} 
                                        zoom={currentMapZoom} 
                                        mapData={mapData} 
                                        heatmapLayerRef={heatmapLayerRef} 
                                      />

                                      {/* === Individual User Markers using react-leaflet <Marker> === */}
                                      {mapData?.users?.map(user => {
                                          // Add validation for coordinates before rendering Marker
                                          if (user && typeof user.latitude === 'number' && typeof user.longitude === 'number') {
                                              const iconInfo = getProfessionIcon(user.profession);
                                              const customIcon = createDivIcon(iconInfo);
                                              // console.log(`[HomePage] Rendering Marker for user ${user.id} (${user.name}) with profession ${user.profession || 'N/A'}, Icon:`, customIcon); // REMOVE DEBUG LOG
                                              return (
                                                  <Marker 
                                                    key={user.id} 
                                                    position={[user.latitude, user.longitude]}
                                                    icon={customIcon} // Apply the custom icon
                                                  >
                                                      <Popup>
                                                          {/* CHANGE: Show only profession */}
                                                          {user.profession || 'Profession N/A'}
                                                      </Popup>
                                                  </Marker>
                                              );
                                          }
                                          return null; // Don't render marker if coordinates are invalid
                                      })}
                                      
                                      {/* === Summary Marker (Kept for reference) === */}
                                      {/* This could conflict visually with heatmap/many markers */}
                                      {/* {mapData && (
                                          <CircleMarker 
                                              center={currentMapCenter}
                                              radius={10} 
                                              pathOptions={{ color: '#0d6efd', fillColor: '#0d6efd', fillOpacity: 0.7 }}
                                          >
                                             <Popup> 
                                                {/* ... popup content ... * /}
                                             </Popup>
                                          </CircleMarker>
                                      )} */}
                                  </MapContainer>
                              </div>
                              {/* Info text below map - WITH profession stats */}
                              <div className="p-3 bg-light-subtle text-center small text-muted">
                                 {isLoading ? (
                                    <div className="mb-0"><Spinner animation="border" size="sm" className="me-2"/>Loading map data...</div>
                                  ) : mapData ? (
                                    <div>
                                      <p className="mb-1">Showing {mapData.stats.totalCount} professionals near zip code {zipCode}:</p>
                                      <ul className="list-inline mb-0">
                                        {Object.entries(mapData.stats.professionCounts).map(([profession, count]) => {
                                          const iconInfo = getProfessionIcon(profession);
                                          return (
                                            <li key={profession} className="list-inline-item me-3">
                                              <FontAwesomeIcon 
                                                icon={iconInfo.icon} 
                                                className="me-1" 
                                                style={{ color: iconInfo.color }} // Apply color to icon
                                              />
                                              <span className="text-capitalize">{profession.replace('_', ' ')}</span>: {count}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  ) : searchError ? (
                                     <p className="mb-0 text-danger"> <FontAwesomeIcon icon={faInfoCircle} className="me-1"/> {searchError}</p>
                                  ) : (
                                    <p className="mb-0">Enter a US zip code to preview nearby professionals.</p>
                                  )}
                              </div>
                          </Card.Body>
                      </Card>
                  </Col>
              </Row>
          </Container>
          {/* Optional: Add Wave SVG or CSS equivalent here if desired */} 
          {/* <div className="wave-bottom">...</div> */} 
      </section>

      {/* Value Proposition Section (Why Join) - Adapted from Flask */} 
      <section className="py-5" id="features"> {/* Reusing id="features" as in Flask */} 
          <Container className="py-lg-5"> {/* Added padding */} 
              <div className="text-center mb-5">
                  <h2 className="fw-bold">Why Join DataNest?</h2>
                  <p className="lead text-muted">Connect with real people who share your professional interests</p>
              </div>
              <Row className="g-4">
                  <Col md={4}>
                      <Card className="h-100 border-0 shadow-sm hover-card">
                          <Card.Body className="text-center p-4">
                              <div className="feature-icon-flask bg-primary bg-opacity-10 text-primary mb-3">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" />
                              </div>
                              <h3 className="h4 fw-bold">Local Connections</h3>
                              <p className="text-muted">Find data professionals within your city for in-person meetups, collaborations, and knowledge sharing.</p>
                          </Card.Body>
                      </Card>
                  </Col>
                  <Col md={4}>
                      <Card className="h-100 border-0 shadow-sm hover-card">
                          <Card.Body className="text-center p-4">
                              <div className="feature-icon-flask bg-primary bg-opacity-10 text-primary mb-3">
                                  <FontAwesomeIcon icon={faUserShield} size="2x" />
                              </div>
                              <h3 className="h4 fw-bold">Privacy First</h3>
                              <p className="text-muted">Your exact location is never shared. Our system uses location offsetting for enhanced privacy and security.</p>
                          </Card.Body>
                      </Card>
                  </Col>
                  <Col md={4}>
                      <Card className="h-100 border-0 shadow-sm hover-card">
                          <Card.Body className="text-center p-4">
                              <div className="feature-icon-flask bg-primary bg-opacity-10 text-primary mb-3">
                                  <FontAwesomeIcon icon={faHandshake} size="2x" />
                              </div>
                              <h3 className="h4 fw-bold">Professional Growth</h3>
                              <p className="text-muted">Access mentorship, job opportunities, and technical discussions with experts in your field.</p>
                          </Card.Body>
                      </Card>
                  </Col>
              </Row>
          </Container>
      </section>

      {/* How It Works Section - Adapted from Flask */}
      <section className="py-5 bg-light" id="how-it-works">
          <Container className="py-lg-5"> {/* Added padding */} 
              <div className="text-center mb-5">
                  <h2 className="fw-bold">How DataNest Works</h2>
                  <p className="lead text-muted">A privacy-focused approach to professional networking</p>
              </div>
              <Row className="g-4 align-items-center">
                  <Col lg={6} className="order-lg-2 mb-4 mb-lg-0"> {/* Image right */} 
                      <img src="/images/p1.png" alt="DataNest Illustration" className="img-fluid rounded-4 shadow-sm"/>
                  </Col>
                  <Col lg={6} className="order-lg-1"> {/* Steps left */} 
                      <div className="d-flex mb-4">
                          <div className="step-number-flask flex-shrink-0">1</div>
                          <div className="ms-3">
                              <h3 className="h5 fw-bold">Create Your Profile</h3>
                              <p className="text-muted mb-0">Sign up and share your professional information, interests, and networking preferences.</p>
                          </div>
                      </div>
                      <div className="d-flex mb-4">
                          <div className="step-number-flask flex-shrink-0">2</div>
                          <div className="ms-3">
                              <h3 className="h5 fw-bold">Set Your Location</h3>
                              <p className="text-muted mb-0">Provide your city location, which we offset for privacy while maintaining relevant connections.</p>
                          </div>
                      </div>
                      <div className="d-flex mb-4">
                          <div className="step-number-flask flex-shrink-0">3</div>
                          <div className="ms-3">
                              <h3 className="h5 fw-bold">Discover Connections</h3>
                              <p className="text-muted mb-0">Browse the interactive map to find data professionals with similar interests in your area.</p>
                          </div>
                      </div>
                      <div className="d-flex">
                          <div className="step-number-flask flex-shrink-0">4</div>
                          <div className="ms-3">
                              <h3 className="h5 fw-bold">Initiate Contact</h3>
                              <p className="text-muted mb-0">Send a wave to start a conversation and arrange meetups or collaboration opportunities.</p>
                          </div>
                      </div>
                  </Col>
              </Row>
          </Container>
      </section>

      {/* Privacy Explanation Section - Adapted from Flask */}
      <section className="py-5">
          <Container className="py-lg-3"> {/* Adjusted padding */} 
              <Row className="align-items-center">
                  <Col lg={5} className="mb-4 mb-lg-0"> {/* Card left */} 
                      <Card className="border-0 shadow-sm">
                          <Card.Body className="p-4">
                              <h2 className="h3 fw-bold mb-3">Your Privacy Is Our Priority</h2>
                              <div className="d-flex align-items-center mb-3 p-2 rounded bg-primary bg-opacity-10"> {/* Enhanced notice */} 
                                  <FontAwesomeIcon icon={faShieldAlt} className="text-primary me-2 flex-shrink-0" size="lg"/>
                                  <p className="mb-0 fw-bold">Your exact location is never revealed</p>
                              </div>
                              <p className="text-muted mb-4">At DataNest, we use location offsetting technology that adds a small random adjustment to your actual coordinates. This ensures you can connect with nearby professionals without revealing your precise location.</p>
                              <div className="d-flex align-items-center mb-2"> {/* Checkmarks */} 
                                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 flex-shrink-0" />
                                  <p className="mb-0 text-muted">Only city-level location is shared in profiles</p>
                              </div>
                              <div className="d-flex align-items-center mb-2">
                                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 flex-shrink-0" />
                                  <p className="mb-0 text-muted">Contact only happens after mutual agreement</p>
                              </div>
                              <div className="d-flex align-items-center">
                                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 flex-shrink-0" />
                                  <p className="mb-0 text-muted">Control who can see your profile information</p>
                              </div>
                          </Card.Body>
                      </Card>
                  </Col>
                  <Col lg={7}> {/* Image right */} 
                      <img src="/images/p2.png" alt="Privacy Illustration" className="img-fluid rounded-4 shadow-sm"/>
                  </Col>
              </Row>
          </Container>
      </section>

      {/* Call to Action Section - Adapted from Flask */}
      <section className="py-5 bg-primary text-white">
          <Container className="py-lg-5 text-center"> {/* Added padding */} 
              <h2 className="display-5 fw-bold mb-4">Ready to connect with data professionals in your area?</h2>
              <p className="lead mb-4">Join the DataNest network today and discover new opportunities for collaboration and growth.</p>
              <Button 
                  onClick={() => handleNavigation('/register')}
                  variant="light" 
                  size="lg" 
                  className="rounded-pill px-5 py-3 shadow"
              >
                  <FontAwesomeIcon icon={faUserPlus} className="me-2" /> Join the Network
              </Button>
              {!isAuthenticated && (
                <p className="mt-3">Already a member? 
                  <Link to="/login" className="text-white fw-bold ms-1">Login here</Link>
                </p>
              )}
          </Container>
      </section>
    </MainLayout>
  );
};

export default HomePage;
